import { useCallback, useEffect, useState } from 'react';

import Cookies from 'js-cookie';

import {
    LIVE_CHAT_STARTED_COOKIE,
    OPEN_LIVE_CHAT_HASH,
    OPEN_LIVE_CHAT_QUERY_PARAM,
    SESSION_COOKIE_OPTIONS,
} from './constants';
import {
    addCustomVariable,
    areChatAgentsAvailable,
    closeLiveChat,
    getQueryParam,
    hideLiveChat,
    initLiveChatGlobalVars,
    isChatRunning,
    isChatWindowMaximized,
    openLiveChat,
    runOnCustomerStatusChanged,
    runOnLiveChatReady,
    runOnNewEvent,
    runOnVisibilityChanged,
    sendCustomVariables,
} from './utils';

type Params = {
    sessionToken?: string;
    businessPartnerId?: string;
};

export const useLiveChat = ({ sessionToken, businessPartnerId }: Params) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isAvailable, setIsAvailable] = useState(false);
    const [messageCount, setMessageCount] = useState<number>();

    const [chatGroup, setChatGroup] = useState<number>(0);
    const [isChatStarted, setIsChatStarted] = useState(false);

    const initChatStartedStatus = useCallback(() => {
        const cookieValue = Cookies.get(LIVE_CHAT_STARTED_COOKIE);

        setIsChatStarted(Boolean(cookieValue));

        return Boolean(cookieValue) ? parseInt(cookieValue) : undefined;
    }, []);

    const setUserVariables = useCallback(() => {
        addCustomVariable('sessionToken', sessionToken || '1');
        addCustomVariable('businessPartnerId', businessPartnerId || '1');
    }, [businessPartnerId, sessionToken]);

    const syncChatStatus = useCallback(() => {
        const cookieValue = Cookies.get(LIVE_CHAT_STARTED_COOKIE);
        const isChatStartedByCookie = Boolean(cookieValue);

        if (isChatStartedByCookie) {
            if (!isChatStarted) {
                const initChatGroup = initChatStartedStatus();
                setChatGroup(initChatGroup);
            }
        } else {
            if (isChatStarted) {
                const initChatGroup = initChatStartedStatus();
                setChatGroup(initChatGroup);
                hideLiveChat();
            }
        }
    }, [initChatStartedStatus, isChatStarted]);

    const setChatStartedStatus = useCallback(
        (isStarted: boolean) => {
            setIsChatStarted(isStarted);

            if (isStarted) {
                Cookies.set(LIVE_CHAT_STARTED_COOKIE, chatGroup.toString(), SESSION_COOKIE_OPTIONS);
            } else {
                Cookies.remove(LIVE_CHAT_STARTED_COOKIE, SESSION_COOKIE_OPTIONS);
            }
        },
        [chatGroup],
    );

    const initLiveChatEvents = useCallback(() => {
        runOnLiveChatReady(() => {
            setIsLoaded(true);
            setChatStartedStatus(isChatRunning());
            setIsAvailable(areChatAgentsAvailable());

            const shouldOpenOnLoad = Boolean(
                getQueryParam(OPEN_LIVE_CHAT_QUERY_PARAM) || window.location.hash === OPEN_LIVE_CHAT_HASH,
            );

            if (shouldOpenOnLoad) {
                openLiveChat();
            }
        });

        runOnVisibilityChanged((state) => {
            if (state.visibility === 'maximized') {
                setMessageCount(undefined);

                if (!isChatStarted) {
                    setUserVariables();
                    sendCustomVariables();
                }
            }
        });

        runOnCustomerStatusChanged((state) => {
            if (state.status === 'chatting') {
                setChatStartedStatus(true);
            } else if (state.status === 'browsing' && isChatStarted) {
                setChatStartedStatus(false);
                closeLiveChat();
            }
        });

        runOnNewEvent((event) => {
            if (event.type === 'message' && event.author.type === 'agent') {
                if (isChatWindowMaximized()) {
                    return;
                }

                setMessageCount((currentCount) => (currentCount || 0) + 1);
            }
        });
    }, [isChatStarted, setChatStartedStatus, setUserVariables]);

    useEffect(() => {
        const hashChangeHandler = () => {
            if (window.location.hash === OPEN_LIVE_CHAT_HASH) {
                openLiveChat();
            }
        };

        window.addEventListener('hashchange', hashChangeHandler);

        return () => {
            window.removeEventListener('hashchange', hashChangeHandler);
        };
    }, []);

    useEffect(() => {
        const initChatGroup = initChatStartedStatus();

        initLiveChatGlobalVars(initChatGroup);
        initLiveChatEvents();

        if (initChatGroup) {
            setChatGroup(initChatGroup);
        }
    }, [initChatStartedStatus, initLiveChatEvents]);

    useEffect(() => {
        const chatStatusSyncTimer = window.setInterval(() => {
            syncChatStatus();
        }, 3000);

        return () => {
            window.clearInterval(chatStatusSyncTimer);
        };
    }, [syncChatStatus]);

    return { isLoaded, isAvailable, messageCount, openLiveChat, closeLiveChat };
};
